import { z } from 'zod';
import { PaginatedResponseSchema, PaginationQuerySchema } from './pagination';

/**
 * Контракты Блока 5, под-сценарий «Фурнитура и магазины» (каталог фурнитуры).
 * Спека поверхности — ADR-0068 §1. PRD `docs/prd/v4/07-block-5-furniturist.md`.
 *
 * Зеркалит `decors.ts`, но БЕЗ kind-дискриминатора и БЕЗ Lab: фурнитура — единая
 * сущность (P-033-разделение фасад/столешница не нужно). Global_ID и текстовые поля —
 * **verbatim** (`z.string().min(1)`), без regex/transform/normalize (P-001): связь с
 * именами фото в R2 ломается при любой нормализации (урок апреля-2026).
 *
 * Бренд — ГИБРИД (решение владельца, ADR-0068 §«Решения владельца»):
 * - `brand`     — КОД из столбца «Бренд» (`AKS`/`GTV`/`Boyard`/`DTC`/`Hettich`),
 *                 для матча магазинов против `dealers.brands` (ci, на стороне edge).
 * - `brandName` — читаемое имя из столбца «Производитель/Бренд» («Фурнитура AKS»),
 *                 для показа в карточке и текста сообщения магазину.
 *
 * Магазины/города НЕ дублируем: `ShopsResponseSchema`/`ShopContact`/`CitiesResponseSchema`
 * импортируются из `./handles` (общий контракт декор/ручки/краски).
 */

/** Пара «метка → значение» характеристики (только непустые поля строки, §1.2). */
export const FurnitureSpecSchema = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
});
export type FurnitureSpec = z.infer<typeof FurnitureSpecSchema>;

/** Полная карточка товара (Экран 2.5.8). */
export const FurnitureSchema = z.object({
  globalId: z.string().min(1),
  name: z.string().min(1),
  /** Код бренда (матч магазинов). */
  brand: z.string().min(1),
  /** Читаемое имя «Производитель/Бренд» (показ + текст сообщения). */
  brandName: z.string().min(1),
  subcategory: z.string().min(1),
  /** Первое фото-товара (вычисляется на импорте, §1.3). */
  imageFilename: z.string().min(1),
  description: z.string().nullable(),
  url: z.string().nullable(),
  /** Только непустые характеристики; порядок сохраняется массивом пар. */
  specs: z.array(FurnitureSpecSchema),
  // ADR-0095 A2: короткий алиас карточки `<prefix><share_hash>`, навешивается на
  // API-границе (edge/MSW), optional/аддитивно. См. `packages/shared/src/share/short-id.ts`.
  shortId: z.string().optional(),
});
export type Furniture = z.infer<typeof FurnitureSchema>;

/** Лёгкий item для list/автокомплита (Экран 2.5.7). */
export const FurnitureListItemSchema = z.object({
  globalId: z.string().min(1),
  name: z.string().min(1),
  brand: z.string().min(1),
  brandName: z.string().min(1),
  subcategory: z.string().min(1),
  /** R2-ключ для signed URL (как `DecorListItem.primaryPhotoKey`); null = нет фото. */
  primaryPhotoKey: z.string().nullable(),
});
export type FurnitureListItem = z.infer<typeof FurnitureListItemSchema>;

export const FurnitureListResponseSchema = PaginatedResponseSchema(FurnitureListItemSchema);
export type FurnitureListResponse = z.infer<typeof FurnitureListResponseSchema>;

/**
 * Один distinct-бренд для скоупа поиска (Экран 2.5.7). Тот же гибрид, что в
 * `FurnitureSchema` (ADR-0068): `brand` — КОД (матч/value `<select>`), `brandName` —
 * читаемое имя (показ). Оба verbatim (P-001). ДОБАВЛЕНИЕ контракта — ADR-0077.
 */
export const FurnitureBrandSchema = z.object({
  brand: z.string().min(1),
  brandName: z.string().min(1),
});
export type FurnitureBrand = z.infer<typeof FurnitureBrandSchema>;

/** GET /api/furniture/brands → все distinct-бренды каталога, отсортированы по brandName. */
export const FurnitureBrandsResponseSchema = z.object({
  brands: z.array(FurnitureBrandSchema),
});
export type FurnitureBrandsResponse = z.infer<typeof FurnitureBrandsResponseSchema>;

/**
 * GET /api/furniture — query string: подстрока по «Название» (+ опц. фильтр по коду бренда).
 *
 * `name` — БЕЗ `.min(1)` (намеренно, паттерн `DecorNameQuerySchema`/ADR-0067): пустая
 * строка = «поиск не задан», не должна ронять общий list-эндпоинт `400`-ом. Семантику
 * «blank = no-op» держит обработчик (edge/MSW), мин. длина 2 — забота клиента, не контракта.
 */
export const FurnitureListQuerySchema = z
  .object({
    name: z.string().optional(),
    brand: z.string().optional(),
  })
  .merge(PaginationQuerySchema);

/** Output-тип после parse: `limit` всегда заполнен (default=50). */
export type FurnitureListQuery = z.infer<typeof FurnitureListQuerySchema>;
/** Input-тип до parse: `limit` опционален. */
export type FurnitureListQueryInput = z.input<typeof FurnitureListQuerySchema>;

// Магазины/города — переиспуем общий контракт `./handles` (ADR-0068 §1). НЕ
// дублируем и НЕ re-export'им: `ShopsResponseSchema`/`ShopContact`/`CitiesResponseSchema`
// уже доступны из `@pomoshnik/shared` через `./handles`. Повторный re-export здесь
// создал бы конфликт `export *` в `index.ts` (символ стал бы ambiguous и был бы
// молча исключён, сломав существующих потребителей — R1). Потребители фурнитуры
// импортируют их из `./handles` напрямую (как `api-client/decors.ts`).
